import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
  Alert,
  Autocomplete,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useQueries, useQuery } from "@tanstack/react-query";
import * as React from "react";

import { useDomainServices } from "@/providers/DomainServices";
import { RobotoTableCell, RobotoTableRow } from "@/shared/components";
import { AlertDialog } from "@/shared/components/AlertDialog";
import { orgUsersQueryOptions } from "@/shared/domain/orgs/hooks";
import type { UserGroupRecord } from "@/shared/domain/userGroups";
import {
  useAddUserGroupMembers,
  useCreateUserGroup,
  useDeleteUserGroup,
  useRemoveUserGroupMembers,
  useUpdateUserGroup,
  useUserGroups,
} from "@/shared/domain/userGroups/hooks";
import { RobotoConflictException } from "@/shared/http";

import styles from "./UserGroupsPanel.module.css";

interface MemberOption {
  label: string;
  value: string;
}

const explanation =
  "User Groups define who a feature should be rolled out to.\n\n" +
  "The groups should be fairly narrowly-constrained, and they should become progressively larger so that we have a " +
  "'ramp' that features can progress down before they go out.";

export function UserGroupsPanel() {
  const { orgService } = useDomainServices();

  const { data: groups = [], isLoading, error } = useUserGroups();
  const { data: allOrgs = [] } = useQuery({
    queryKey: ["allOrgs"],
    queryFn: ({ signal }) => orgService.getAllOrgs({ abortSignal: signal }),
    staleTime: 30_000,
  });
  const createMutation = useCreateUserGroup();
  const updateMutation = useUpdateUserGroup();
  const addMembersMutation = useAddUserGroupMembers();
  const removeMembersMutation = useRemoveUserGroupMembers();
  const deleteMutation = useDeleteUserGroup();

  // The shared options own the cache key and freshness window for org
  // member lists; this per-org fan-out just spreads them.
  const orgUserQueries = useQueries({
    queries: allOrgs.map((org) => orgUsersQueryOptions(orgService, org.org_id)),
  });

  const userOptions: MemberOption[] = React.useMemo(() => {
    const seen = new Set<string>();
    const options: MemberOption[] = [];
    for (const query of orgUserQueries) {
      for (const orgUser of query.data ?? []) {
        if (!seen.has(orgUser.user.user_id)) {
          seen.add(orgUser.user.user_id);
          const displayName = orgUser.user.name
            ? `${orgUser.user.name} (${orgUser.user.user_id})`
            : orgUser.user.user_id;
          options.push({
            label: `User: ${displayName}`,
            value: orgUser.user.user_id,
          });
        }
      }
    }
    return options.sort((a, b) => a.label.localeCompare(b.label));
  }, [orgUserQueries]);

  const orgOptions: MemberOption[] = React.useMemo(
    () =>
      allOrgs.map((o) => ({
        label: `Org: ${o.name} (${o.org_id})`,
        value: o.org_id,
      })),
    [allOrgs],
  );

  const allMemberOptions = React.useMemo(
    () => [...userOptions, ...orgOptions],
    [userOptions, orgOptions],
  );

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [editingGroup, setEditingGroup] =
    React.useState<UserGroupRecord | null>(null);
  const [name, setName] = React.useState("");
  const [selectedMembers, setSelectedMembers] = React.useState<MemberOption[]>(
    [],
  );
  const [formError, setFormError] = React.useState<string | null>(null);

  const [deleteTarget, setDeleteTarget] =
    React.useState<UserGroupRecord | null>(null);
  const [deleteError, setDeleteError] = React.useState<string | null>(null);

  function memberOptionFromId(id: string): MemberOption {
    return (
      allMemberOptions.find((o) => o.value === id) ?? { label: id, value: id }
    );
  }

  function openCreate() {
    setEditingGroup(null);
    setName("");
    setSelectedMembers([]);
    setFormError(null);
    setDialogOpen(true);
  }

  function openEdit(group: UserGroupRecord) {
    setEditingGroup(group);
    setName(group.name);
    setSelectedMembers(group.members.map(memberOptionFromId));
    setFormError(null);
    setDialogOpen(true);
  }

  function closeDialog() {
    setDialogOpen(false);
    setFormError(null);
  }

  async function handleSubmit() {
    if (!name.trim()) {
      setFormError("Name is required.");
      return;
    }
    const members = selectedMembers.map((m) => m.value);
    try {
      if (editingGroup) {
        // Editing a group splits into up to two server calls: an
        // optional rename via the bulk update endpoint when the name
        // changed, then reconcile membership via the granular
        // add/remove endpoints. The granular endpoints take only
        // deltas, so concurrent edits by another admin to members this
        // admin did not touch are preserved. Each step is skipped when
        // there is nothing to do, so a save with no changes issues zero
        // round-trips.
        if (name.trim() !== editingGroup.name) {
          await updateMutation.mutateAsync({
            userGroupId: editingGroup.user_group_id,
            updates: { name: name.trim() },
          });
        }

        // Compute the delta between the group's persisted members and
        // the admin's current dialog selection: ids newly selected are
        // added, ids dropped from the prior list are removed.
        const previousIds = new Set(editingGroup.members);
        const nextIds = new Set(members);
        const toAdd = members.filter((id) => !previousIds.has(id));
        const toRemove = editingGroup.members.filter((id) => !nextIds.has(id));

        if (toAdd.length > 0) {
          await addMembersMutation.mutateAsync({
            userGroupId: editingGroup.user_group_id,
            request: { members: toAdd },
          });
        }
        if (toRemove.length > 0) {
          await removeMembersMutation.mutateAsync({
            userGroupId: editingGroup.user_group_id,
            request: { members: toRemove },
          });
        }
      } else {
        await createMutation.mutateAsync({ name: name.trim(), members });
      }
      closeDialog();
    } catch (err) {
      // Any failed step (rename, add, remove, or create) leaves the
      // dialog open with the admin's input intact and surfaces the
      // server's message inline. On edit, prior steps that succeeded
      // are not rolled back; the admin re-submits to retry the rest.
      setFormError(err instanceof Error ? err.message : "An error occurred.");
    }
  }

  const isPending =
    createMutation.isPending ||
    updateMutation.isPending ||
    addMembersMutation.isPending ||
    removeMembersMutation.isPending;
  const submitLabel = editingGroup ? "Save" : "Create";

  function memberSummary(group: UserGroupRecord): string {
    if (!group.members.length) {
      return "—";
    }
    if (group.members.length <= 5) {
      return group.members.map((id) => memberOptionFromId(id).label).join(", ");
    }
    return `${group.members.length} members`;
  }

  return (
    <div>
      <div className={styles.toolbar}>
        <Typography
          variant="body2"
          color="text.secondary"
          className={styles.explanation}
        >
          {explanation.trim()}
        </Typography>
        <Button variant="contained" onClick={openCreate}>
          Add User Group
        </Button>
      </div>

      {error && (
        <Alert severity="error" className={styles.alert}>
          {error.message}
        </Alert>
      )}
      {deleteError && (
        <Alert
          severity="error"
          onClose={() => setDeleteError(null)}
          className={styles.alert}
        >
          {deleteError}
        </Alert>
      )}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <RobotoTableCell>Name</RobotoTableCell>
              <RobotoTableCell>Members</RobotoTableCell>
              <RobotoTableCell>Actions</RobotoTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <RobotoTableRow>
                <RobotoTableCell colSpan={3} align="center">
                  <CircularProgress size="1.25rem" />
                </RobotoTableCell>
              </RobotoTableRow>
            )}
            {groups.map((group) => (
              <RobotoTableRow key={group.user_group_id}>
                <RobotoTableCell>{group.name}</RobotoTableCell>
                <RobotoTableCell>{memberSummary(group)}</RobotoTableCell>
                <RobotoTableCell>
                  <div className={styles.rowActions}>
                    <Button
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => openEdit(group)}
                    >
                      Edit
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => {
                        setDeleteTarget(group);
                        setDeleteError(null);
                      }}
                    >
                      Delete
                    </Button>
                  </div>
                </RobotoTableCell>
              </RobotoTableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingGroup ? "Edit User Group" : "Add User Group"}
        </DialogTitle>
        <DialogContent className={styles.dialogContent}>
          {formError && <Alert severity="error">{formError}</Alert>}
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            fullWidth
          />
          <Autocomplete<MemberOption, true, false, false>
            multiple
            options={allMemberOptions}
            getOptionLabel={(o) => o.label}
            isOptionEqualToValue={(a, b) => a.value === b.value}
            value={selectedMembers}
            onChange={(_, newValue) => {
              setSelectedMembers(newValue);
            }}
            renderTags={(tagValue, getTagProps) =>
              tagValue.map((opt, index) => {
                const { key, ...tagProps } = getTagProps({ index });
                return (
                  <Chip
                    key={key}
                    label={opt.label}
                    size="small"
                    {...tagProps}
                  />
                );
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Members"
                placeholder="Org ID or user ID…"
              />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              void handleSubmit();
            }}
            disabled={isPending}
          >
            {isPending ? <CircularProgress size="1rem" /> : submitLabel}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog
        dialogOpen={deleteTarget !== null}
        handleClose={() => setDeleteTarget(null)}
        dialogTitle="Delete User Group"
        dialogText={`Are you sure you want to delete "${deleteTarget?.name ?? ""}"? This cannot be undone.`}
        dialogAction={async () => {
          if (!deleteTarget) {
            return;
          }
          try {
            await deleteMutation.mutateAsync(deleteTarget.user_group_id);
          } catch (err) {
            // The server rejects deletion of a group still associated
            // with a feature flag with a 409 Conflict; rephrase that
            // for the admin so they know to detach the group from any
            // referencing flag first. All other failures pass through
            // the server's message as-is.
            if (err instanceof RobotoConflictException) {
              setDeleteError(
                `Cannot delete "${deleteTarget.name}": it is referenced by a feature flag.`,
              );
            } else {
              setDeleteError(
                err instanceof Error
                  ? err.message
                  : "Failed to delete user group.",
              );
            }
          }
        }}
      />
    </div>
  );
}
